/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { useNavigate } from "react-router-dom";

// components imports
import { Button, Col, Descriptions, Grid, Row, Table } from "antd";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";
import { ColumnsType } from "antd/es/table";

// misc imports
import { useUserInfo } from "@/api/user";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { CURRENCY_SYMBOLS, NAV_PROJECT_SINGLE_VIEW, PAGINATION_OPTIONS } from "@/constants";
import { useGetInvoices } from "@/api/user";
import { numberToFixed } from "@/utils/number/numberToFixed";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { ICurrency } from "@/interfaces/SubscriptionPlans";
import { IProject } from "@/interfaces/Project.interface";
import { useWorkspacesList } from "@/api/workspaces";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

const { useBreakpoint } = Grid;

interface IInvoiceDataTable {
  id: string;
  amount: number;
  creation_date: string;
  currency: ICurrency;
  invoice_pdf: string;
  project_uuid: string;
  subscription_plan: string;
}

export const MyInvoices = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const tokensInstance = Tokens;
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const [projectsData, setProjectsData] = useState<IProject[]>([]);
  const tokenSub = tokensInstance.getTokenSub(accessToken) as string;
  const { data: invoicesData, isLoading: isLoadingInvoicesData } = useGetInvoices(tokenSub);
  const { data: userInfo } = useUserInfo(tokenSub);
  const { data: workspaces } = useWorkspacesList();

  const isScreenBelowSM = () => screens.xs || (screens.sm && !screens.md) || false;
  const getProjectName = (projectId: string): string | null => {
    if (!projectsData) {
      return null;
    }

    return projectsData.find((project) => project.uuid === projectId)?.title || null;
  };

  // Get WorkspaceId and ProjectId based on method param project
  const getProjectParams = (projectId: string): { workspaceId: string; projectId: string } => {
    const workspaceId = workspaces?.find((workspace) => workspace.projects.find((project) => project.uuid === projectId))?.uuid;
    return {
      workspaceId: workspaceId as string,
      projectId: projectId as string,
    };
  };

  // USE EFFECTS
  useEffect(() => {
    if (!workspaces) {
      return;
    }
    const _projects = workspaces.flatMap((workspace) => workspace.projects.map((project) => ({ ...project })));

    setProjectsData(_projects);
  }, [workspaces]);

  // Columns configuration for the invoices table
  const columns: ColumnsType<IInvoiceDataTable> = [
    {
      title: t("subscriptions_screen.created_at"),
      dataIndex: "creation_date",
      key: "creation_date",
      render: (created: string) => getFormattedDate(created, userInfo?.timezone),
    },
    {
      title: t("subscriptions_screen.total"),
      dataIndex: "amount",
      key: "amount",
      render: (text: number, record: IInvoiceDataTable) => `${parse(CURRENCY_SYMBOLS[record.currency].icon)} ${numberToFixed(text)}`,
      responsive: ["md"],
    },
    {
      title: t("subscriptions_screen.project"),
      dataIndex: "project_uuid",
      key: "project_uuid",
      responsive: ["md"],
      render: (projectId: string) => {
        const params = getProjectParams(projectId);
        if (!params.workspaceId) {
          return t("subscriptions_screen.project_not_found");
        }
        return (
          <Button
            className="px-0"
            onClick={() =>
              navigate(
                `${replaceUrlParams(NAV_PROJECT_SINGLE_VIEW, {
                  ...params,
                })}`, { state: { canGoBack: true } }
              )
            }
            type="link"
          >
            {getProjectName(projectId)}
          </Button>
        );
      },
    },
    {
      title: t("subscriptions_screen.plan"),
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      responsive: ["md"],
    },
    {
      title: t("subscriptions_screen.actions"),
      dataIndex: "invoice_pdf",
      key: "invoice_pdf",
      width: 150,
      render: (text: string) => (
        <Button type="link" className="m-0 px-0" href={text} target="_blank">
          {t("Download")}
        </Button>
      ),
    },
  ];

  if (!invoicesData && isLoadingInvoicesData) {
    return <DataLoadingSkeleton />;
  }

  return (
    <Row>
      <Col xs={24}>
        <Table
        size="small"
          bordered
          rowKey="id"
          columns={columns}
          dataSource={invoicesData}
          pagination={{ ...PAGINATION_OPTIONS }}
          expandable={{
            rowExpandable: () => isScreenBelowSM(),
            showExpandColumn: isScreenBelowSM(),
            expandedRowRender: (record) => {
              return (
                <Descriptions bordered size="small">
                  <Descriptions.Item span={3} label={t("subscriptions_screen.total")}>{`${parse(
                    CURRENCY_SYMBOLS[record.currency]?.icon
                  )} ${numberToFixed(record.amount)}`}</Descriptions.Item>
                  <Descriptions.Item span={3} label={t("subscriptions_screen.project")}>
                    {getProjectName(record.project_uuid)}
                  </Descriptions.Item>
                  <Descriptions.Item span={3} label={t("subscriptions_screen.plan")}>
                    {record.subscription_plan}
                  </Descriptions.Item>
                </Descriptions>
              );
            },
          }}
        />
      </Col>
    </Row>
  );
};
